// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PermissionsService } from "src/app/modules/permissions/permissions.service";
import { UsersService } from "src/app/modules/users/users.service";
import { IPermission } from "src/app/shared/models/permission/permission.interface";
import { Role } from "src/app/shared/models/role/role.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-add-role",
  templateUrl: "./add-role.page.html",
  styleUrls: ["./add-role.page.scss"],
})
export class AddRoleComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add role form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add role form

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    public helperService: HelperService,
    public configService: ConfigService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private usersService: UsersService,
    private router: Router,
    private toastrService: ToastrService,
    private permissionsService: PermissionsService,
    private apiService: ApiService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("roles.add.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.subscriptions.push(
      this.permissionsService.getList(0, -1, "", "").subscribe((res) => {
        if (res.status) {
          // Init form
          this.initForm(res.data.items);
          return;
        }
        // @todo translate
        this.toastrService.error(
          "Errore!",
          "Impossibile recuperare i permessi. Prova tra un attimo"
        );
      })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Group given 'arr' array by given 'prop' property value
   * and return the grouped array
   *
   * @since 1.0.0
   */
  getGroups(arr: any[], prop: string): any[] {
    const map = new Map(Array.from(arr, (obj) => [obj[prop], []]));
    arr.forEach((obj) => map.get(obj[prop]).push(obj));
    return Array.from(map);
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(permissions: IPermission[]): void {
    // Set model
    this.model = new Role();

    // Build permissions' groups
    const groupedPermissions: any[] = this.getGroups(permissions, "module");
    const groups: any[] = [];
    groupedPermissions.forEach((permissionGroup) => {
      const group: any[] = [];
      group.push({
        className: "col-12 min-height-auto",
        template: `
          <div>
            <strong>${this.translateService.instant(
              "roles.fields.permissionsForModule",
              { module: permissionGroup[0] }
            )}:</strong>
          </div>
          `,
      });
      permissionGroup[1].forEach((permission) => {
        const items = {
          className: "col-12 col-md-4",
          key: `permissions[${permissionGroup[0]}][${permission._id}]`,
          fieldGroupClassName: "permission-wrapper",
          fieldGroup: [
            {
              className: "col-12",
              key: `_id`,
              type: "checkbox",
              checked: false,
              templateOptions: {
                label: permission.name,
                required: false,
                checked: false,
              },
            },
            {
              className: "col-12",
              key: "expiresAt",
              type: "datepicker",
              templateOptions: {
                placeholder: "Scadenza permesso",
                label: "Scadenza permesso",
                min: new Date(),
              },
              hideExpression: (model: any, formState: any) => {
                if (
                  this.model &&
                  this.model.permissions &&
                  this.model.permissions[permissionGroup[0]] &&
                  this.model.permissions[permissionGroup[0]][permission._id]._id
                ) {
                  return false;
                }
                return true;
              },
            },
            {
              className: "col-12",
              key: "relatedObjId",
              type: "input",
              templateOptions: {
                placeholder: "ID oggetto correlato",
                label: "ID oggetto correlato",
                min: new Date(),
                required: false,
              },
              hideExpression: (model: any, formState: any) => {
                if (
                  this.model &&
                  this.model.permissions &&
                  this.model.permissions[permissionGroup[0]] &&
                  this.model.permissions[permissionGroup[0]][permission._id]._id
                ) {
                  return false;
                }
                return true;
              },
            },
            {
              className: "col-12",
              key: "relatedObjRef",
              type: "select",
              templateOptions: {
                placeholder: "Referenza oggetto correlato",
                label: "Referenza oggetto correlato",
                required: false,
                // @todo update this once web-app modules are complted
                options: [
                  {
                    value: "User",
                    label: "Utenti",
                  },
                  {
                    value: "Permission",
                    label: "Permessi",
                  },
                  {
                    value: "Role",
                    label: "Ruoli",
                  },
                  {
                    value: "Company",
                    label: "Società",
                  },
                  {
                    value: "Property",
                    label: "Immobili",
                  },
                  {
                    value: "Supplier",
                    label: "Fornitori",
                  },
                  {
                    value: "Service",
                    label: "Servizi",
                  },
                ],
              },
              hideExpression: (model: any, formState: any) => {
                if (
                  this.model &&
                  this.model.permissions &&
                  this.model.permissions[permissionGroup[0]] &&
                  this.model.permissions[permissionGroup[0]][permission._id]._id
                ) {
                  return false;
                }
                return true;
              },
            },
            {
              className: "col-12",
              key: "relatedObjSection",
              type: "input",
              templateOptions: {
                placeholder: "Sezioni oggetto correlato",
                label: "Sezioni oggetto correlato",
                required: false,
              },
              hideExpression: (model: any, formState: any) => {
                if (
                  this.model &&
                  this.model.permissions &&
                  this.model.permissions[permissionGroup[0]] &&
                  this.model.permissions[permissionGroup[0]][permission._id]._id
                ) {
                  return false;
                }
                return true;
              },
            },
          ],
        };
        group.push(items);
      });
      groups.push(...group);
    });

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("roles.fields.name"),
              placeholder: this.translateService.instant("roles.fields.name"),
              required: true,
              minLength: 3,
              maxLength: 50,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant("roles.fields.type"),
              placeholder: this.translateService.instant("roles.fields.type"),
              required: true,
              options: [
                {
                  value: "admin",
                  label: "Admin",
                },
                {
                  value: "operator",
                  label: "Operatore",
                },
                {
                  value: "master",
                  label: "Master",
                },
                {
                  value: "collaborator",
                  label: "Collaboratore",
                },
                {
                  value: "temporary",
                  label: "Temporaneo",
                },
                {
                  value: "user",
                  label: "Utente",
                },
              ],
            },
          },
          ...groups,
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant("roles.fields.description"),
              placeholder: this.translateService.instant(
                "roles.fields.description"
              ),
              maxLength: 500,
              required: true,
              rows: 2,
            },
            expressionProperties: {
              "templateOptions.description":
                '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      console.warn(model);
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.usersService
        .create(model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res.status) {
              this.toastrService.success(res.message);
              setTimeout(() => {
                const lang = this.translateService.currentLang;
                this.router.navigate([
                  lang,
                  "app",
                  "roles",
                  "detail",
                  res.data._id,
                ]);
              }, this.configService.redirectDelayAfterCreation);
              return;
            }
          },
          (err) => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
